import React, { useState, useEffect } from "react";
import { CpuChipIcon as Bot } from "@heroicons/react/24/outline";

interface TypingAnimationProps {
  language?: "es" | "fr" | "en";
  aiDisplayName?: string;
  isTyping?: boolean;
  progressMessage?: string; // Mensaje específico del backend
  inline?: boolean; // Nuevo prop para modo integrado
  showLogo?: boolean; // Para mostrar el logo de VettConsult
}

/**
 * Unified typing animation component for AI responses
 * Features a green background to match the page theme and shows as a chat message
 */
const TypingAnimation: React.FC<TypingAnimationProps> = ({
  language = "en",
  aiDisplayName = "NALA AI",
  isTyping = true,
  progressMessage,
  inline = false, // Por defecto false para mantener compatibilidad
  showLogo = false, // Por defecto false
}) => {
  const [currentDots, setCurrentDots] = useState(1);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  // Multilingual comprehensive progress messages that rotate
  const progressMessages = {
    es: [
      "Conectando con VettConsult IA...",
      "Analizando tu consulta veterinaria...",
      "Buscando en la base de conocimientos...",
      "Consultando evidencia científica...",
      "Preparando respuesta especializada...",
      "Verificando información médica...",
      "Finalizando análisis científico...",
    ],
    fr: [
      "Connexion à VettConsult IA...",
      "Analyse de votre consultation vétérinaire...",
      "Recherche dans la base de connaissances...",
      "Consultation des preuves scientifiques...",
      "Préparation de la réponse spécialisée...",
      "Vérification des informations médicales...",
      "Finalisation de l'analyse scientifique...",
    ],
    en: [
      "Connecting to VettConsult AI...",
      "Analyzing your veterinary consultation...",
      "Searching the knowledge base...",
      "Consulting scientific evidence...",
      "Preparing specialized response...",
      "Verifying medical information...",
      "Finalizing scientific analysis...",
    ],
  };

  // Simple typing indicators
  const typingIndicators = {
    es: "está preparando tu respuesta",
    fr: "prépare votre réponse",
    en: "is preparing your response",
  };

  // Animate typing dots (1, 2, 3, repeat)
  useEffect(() => {
    if (!isTyping) return;

    const dotsInterval = setInterval(() => {
      setCurrentDots((prev) => (prev >= 3 ? 1 : prev + 1));
    }, 500);

    return () => clearInterval(dotsInterval);
  }, [isTyping]);

  // Rotate progress messages every 2.5 seconds
  useEffect(() => {
    if (!isTyping) return;

    const messageInterval = setInterval(() => {
      setCurrentMessageIndex(
        (prev) => (prev + 1) % progressMessages[language].length
      );
    }, 2500);

    return () => clearInterval(messageInterval);
  }, [isTyping, language, progressMessages]);

  if (!isTyping) return null;

  // Use specific progress message from backend or rotating messages
  const displayMessage =
    progressMessage || progressMessages[language][currentMessageIndex];

  // Si está en modo inline, solo renderizar el contenido de la animación
  if (inline) {
    return (
      <div className="space-y-3">
        {/* Main progress message */}
        <div>
          <p className="text-sm font-medium text-white mb-3">
            {displayMessage}
          </p>
        </div>

        {/* Typing animation content */}
        <div className="flex items-center space-x-2 py-2">
          {/* Gemini-style writing animation */}
          <div className="flex items-center space-x-1">
            <div className="flex space-x-1">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    i < currentDots
                      ? "bg-emerald-500 animate-pulse scale-110"
                      : "bg-emerald-300/50 scale-90"
                  }`}
                  style={{
                    animationDelay: `${i * 150}ms`,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Subtle cursor blink */}
          <div className="w-0.5 h-4 bg-emerald-500 animate-pulse opacity-75" />

          {/* Flowing gradient background animation */}
          <div className="flex-1 h-1 bg-gradient-to-r from-emerald-200 via-teal-200 to-emerald-200 rounded-full overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-400 to-transparent w-8 animate-ping transform" />
          </div>
        </div>

        {/* Gemini-style subtle progress indicator */}
        <div className="w-full bg-emerald-100 rounded-full h-1">
          <div
            className="bg-gradient-to-r from-emerald-400 to-teal-500 h-1 rounded-full transition-all duration-500 animate-pulse"
            style={{
              width: `${20 + currentMessageIndex * 15}%`,
            }}
          />
        </div>
      </div>
    );
  }

  // Modo independiente (como estaba antes)

  return (
    <div className="flex justify-start group mb-4">
      <div className="max-w-[85%] bg-gradient-to-br from-emerald-50/90 to-teal-50/90 backdrop-blur-lg text-emerald-800 border border-emerald-200/50 rounded-2xl p-5 shadow-lg transition-all duration-300 hover:shadow-xl">
        {/* Header with AI avatar */}
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-white to-emerald-50 border-2 border-emerald-300 flex items-center justify-center shadow-md animate-pulse overflow-hidden">
              {showLogo ? (
                <img
                  src="/images/logo/LogoVettConsultBlanco.webp"
                  alt="VettConsult Logo"
                  className="w-6 h-6 object-contain"
                />
              ) : (
                <Bot className="h-4 w-4 text-emerald-600" />
              )}
            </div>
            <div>
              <span className="font-semibold text-emerald-800">
                {aiDisplayName}
              </span>
              <span className="block text-xs text-emerald-600">
                {typingIndicators[language]}
              </span>
            </div>
          </div>
        </div>

        {/* Main progress message */}
        <div className="mb-4">
          <p className="text-sm font-medium text-white text-center">
            {displayMessage}
          </p>
        </div>

        {/* Typing animation content */}
        <div className="flex items-center space-x-2 py-2">
          {/* Gemini-style writing animation */}
          <div className="flex items-center space-x-1">
            <div className="flex space-x-1">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    i < currentDots
                      ? "bg-emerald-500 animate-pulse scale-110"
                      : "bg-emerald-300/50 scale-90"
                  }`}
                  style={{
                    animationDelay: `${i * 150}ms`,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Subtle cursor blink */}
          <div className="w-0.5 h-4 bg-emerald-500 animate-pulse opacity-75" />

          {/* Flowing gradient background animation */}
          <div className="flex-1 h-1 bg-gradient-to-r from-emerald-200 via-teal-200 to-emerald-200 rounded-full overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-400 to-transparent w-8 animate-ping transform" />
          </div>
        </div>

        {/* Gemini-style subtle progress indicator */}
        <div className="mt-2 w-full bg-emerald-100 rounded-full h-1">
          <div
            className="bg-gradient-to-r from-emerald-400 to-teal-500 h-1 rounded-full transition-all duration-500 animate-pulse"
            style={{
              width: `${20 + currentMessageIndex * 15}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default TypingAnimation;
